import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Gauge, Target, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/Common";
import { band } from "@/components/ResultScore";
import { errMsg } from "@/lib/api";
import { getPassMin, useAtsHistory } from "@/lib/atsScores";
import { runAts } from "@/lib/tailor";
import { ScoreBurst } from "@/components/ScoreBurst";

export const TailorBanner = ({ jobId, job, atsTool, beforeCheck, editing }) => {
  const hint = editing ? "Apply this job's ideas below, then \"Save & re-check ATS\" saves this version as your main resume and scores it again." : "Import your resume below to start tailoring it for this job.";
  const h = useAtsHistory(jobId);
  const score = h.at(-1)?.score ?? null;
  const prev = h.length > 1 ? h.at(-2).score : null;
  const [busy, setBusy] = useState(false);
  const recheck = async () => {
    setBusy(true);
    try {
      if (!(await beforeCheck())) return;
      const s = await runAts(atsTool, jobId);
      if (s == null) toast.error("Couldn't read a score from the check.");
      else if (score != null && score < getPassMin() && s >= getPassMin()) return;
      else if (score != null && s > score) toast.success(`ATS score up ${s - score} points to ${s}%`);
      else toast.success(`ATS score: ${s}%`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fade-up relative mb-5 grid gap-4 rounded-2xl border border-[#D4AF37]/40 bg-gradient-to-r from-[#D4AF37]/15 via-[#141417] to-[#141417] p-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center sm:p-5 sm:pr-12" data-testid="tailor-banner">
      <Link to="/resume-studio" aria-label="Exit tailoring mode" className="absolute right-2 top-2 rounded-full p-2 text-zinc-500 transition-colors hover:bg-white/10 hover:text-zinc-200" data-testid="tailor-exit"><X className="h-4 w-4" /></Link>
      <div className="flex min-w-0 items-start gap-3 pr-8 sm:pr-0">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#D4AF37] text-black"><Target className="h-5 w-5" /></span>
        <div className="min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">Tailoring mode</p>
          <p className="break-words font-display text-base text-zinc-100 md:text-lg" data-testid="tailor-banner-job">{job?.title || "Selected job"}{job?.company && <span className="text-zinc-400"> · {job.company}</span>}</p>
          <p className="text-xs text-zinc-500" data-testid="tailor-banner-hint">{hint}</p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {score != null && (() => { const b = band(score); return (
          <ScoreBurst jobId={jobId}><div className={`flex items-center gap-2 rounded-xl border px-3 py-1.5 ${b.bg}`} data-testid="tailor-banner-score">
            <span className={`font-mono text-xl font-bold ${b.text}`}>{score}%</span>
            <span className="text-[11px] leading-tight text-zinc-400"><span className={`block font-medium ${b.text}`}>{b.label}</span>{prev != null ? `was ${prev}%` : "ATS pass rate"}</span>
          </div></ScoreBurst>); })()}
        {atsTool && <Button size="sm" variant="outline" onClick={recheck} disabled={busy} className="border-[#D4AF37]/50 text-[#F3C649]" data-testid="tailor-recheck-ats">{busy ? <Spinner /> : <Gauge className="h-4 w-4" />}{editing ? "Save & re-check ATS" : score == null ? "Check ATS score" : "Re-check ATS score"}</Button>}
      </div>
    </div>
  );
};
